import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StyledDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { CustomFieldFormBody } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/components/CreateCustomFIle/CustomFieldFormBody';
import {
	CreateCustomFieldDto,
	CreateCustomFieldSchema,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/components/investorData.zod.ts';
import { ENTITY_TABS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import {
	CustomDetailEnum,
	OrganizationCustomFieldResponseDto,
} from '@dealstack/shared/lib/generated';

interface Props {
	opened: boolean;
	onClose: () => void;
	activeTab: ENTITY_TABS;
	editField: OrganizationCustomFieldResponseDto | null;
	isSaving: boolean;
	onCreate: (data: {
		name: string;
		instruction?: string;
		type: CustomDetailEnum;
		isChecked: boolean;
	}) => void;
	onUpdate: (customFieldId: string, data: CreateCustomFieldDto) => void;
	onDelete: (customFieldId: string) => void;
}

export const OrganizationCustomFieldDrawer = ({
	opened,
	onClose,
	activeTab,
	editField,
	isSaving,
	onCreate,
	onUpdate,
	onDelete,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const form = useForm<CreateCustomFieldDto>({
		resolver: zodResolver(CreateCustomFieldSchema),
		defaultValues: { name: '', instruction: '', isChecked: false },
	});

	useEffect(() => {
		form.reset({
			name: editField?.name ?? '',
			instruction: editField?.instruction ?? '',
			isChecked: editField?.isChecked ?? false,
		});
	}, [editField, opened, form]);

	const handleSubmit = (values: CreateCustomFieldDto) => {
		if (editField) {
			onUpdate(editField.id, values);
		} else {
			onCreate({
				name: values.name,
				instruction: values.instruction,
				type:
					activeTab === ENTITY_TABS.INDIVIDUAL
						? CustomDetailEnum.INDIVIDUAL
						: CustomDetailEnum.VEHICLE,
				isChecked: values.isChecked,
			});
		}

		onClose();
	};

	const handleDelete = () => {
		if (!editField) {
			return;
		}

		onDelete(editField.id);
		onClose();
	};

	return (
		<StyledDrawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				editField
					? tMipManage('investor_data.custom_fields.edit_field')
					: tMipManage('investor_data.custom_fields.add_field')
			}
		>
			<CustomFieldFormBody
				form={form}
				isEditMode={!!editField}
				isLoading={isSaving}
				onSubmit={handleSubmit}
				onDelete={handleDelete}
			/>
		</StyledDrawer>
	);
};
